import { PhoneNumberValidType, SupportedCountries } from "@markit/common.utils";
import {
  formatPhoneNumberIntl,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";

// Canonical phone validity check, mirroring MarkitShared's
// checkPhoneNumberValidCountryType (common/utils/phoneUtils.ts) but kept
// local here - that shared version does `require("libphonenumber-js")`,
// which Create React App 5's webpack config mishandles (its module rules
// don't treat the package's .cjs entry file as JS, so it gets bundled as a
// static asset instead of executable code). react-phone-number-input's
// parsePhoneNumber/isValidPhoneNumber are backed by the same
// libphonenumber-js under the hood, just reached via an ES import that
// resolves to a .js file instead of a require() that resolves to .cjs, so
// this is functionally identical without hitting that bundling issue.
export const checkPhoneNumberValidCountryType = (
  phoneNumber: string
): PhoneNumberValidType => {
  if (!phoneNumber) {
    return PhoneNumberValidType.NONE;
  }
  const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
  if (isValidPhoneNumber(phoneNumber)) {
    if (parsedPhoneNumber?.countryCallingCode === "1") {
      return PhoneNumberValidType.US;
    } else {
      return PhoneNumberValidType.INTERNATIONAL;
    }
  } else {
    return PhoneNumberValidType.INVALID;
  }
};

// Clean up the formatting of the phone number to display to the user
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) {
    return "";
  }
  return formatPhoneNumberIntl(phoneNumber) || phoneNumber;
};

// Formats the number from the imported contacts properly for account creation
//
// TODO: MarkitShared has a canonical formatImportedPhoneNumber
// (common/utils/phoneUtils.ts, MarkitShared PR #11 - not yet merged/
// deployed, kept local for now to avoid an extra bit deploy). Swap this for
// the shared function as part of MARK-3799 (webapp import spreadsheet
// scalability ticket) - identical logic, just imports parsePhoneNumberFromString
// from libphonenumber-js directly instead of via react-phone-number-input.
export const formatImportedPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) {
    return "";
  }
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, "");
  const parsedNumber =
    parsePhoneNumber(cleaned) ??
    parsePhoneNumber("+1" + cleaned) ??
    parsePhoneNumber("+" + cleaned);
  const isUSNumber =
    parsedNumber &&
    (parsedNumber.country === "US" || parsedNumber.country === "CA");
  if (isUSNumber) {
    return parsedNumber.number;
  } else {
    const internationalNumber =
      parsePhoneNumber(cleaned) ?? parsePhoneNumber("+" + cleaned);
    return internationalNumber ? internationalNumber.number : cleaned;
  }
};

// Uses react phone number input to check if the number is valid, and not empty
// Also checks if within a valid supported country
export const isPhoneNumberValid = (phoneNumber: string): boolean => {
  if (!phoneNumber) {
    return false;
  }
  const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
  if (
    parsedPhoneNumber?.country &&
    !SupportedCountries.includes(parsedPhoneNumber.country)
  ) {
    return false;
  }
  return isValidPhoneNumber(phoneNumber);
};

export const isValidUSPhoneNumber = (phoneNumber: string) => {
  return (
    checkPhoneNumberValidCountryType(phoneNumber) === PhoneNumberValidType.US
  );
};

/**
 * When input is a complete valid phone number, returns canonical E.164 (e.g. +14155552671).
 * Returns null for partial input while typing or invalid numbers — use this to gate server queries.
 */
export const normalizePhoneInputToE164 = (raw: string): string | null => {
  if (!raw?.trim()) {
    return null;
  }
  const trimmed = raw.trim();
  let parsed = parsePhoneNumber(trimmed);
  if (!parsed) {
    const cleaned = trimmed.replace(/\D/g, "");
    if (!cleaned.length) {
      return null;
    }
    parsed =
      parsePhoneNumber(cleaned) ??
      parsePhoneNumber("+1" + cleaned) ??
      parsePhoneNumber("+" + cleaned);
  }
  if (parsed && isValidPhoneNumber(parsed.number)) {
    return parsed.number;
  }
  return null;
};
